import {
  CalendarIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  PrinterIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'
import classnames from 'classnames'
import moment from 'moment'
import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import APP from 'app'
import { trackEvent } from 'global-utils/Analytics'
import { ProgressBar, Tooltip } from 'ui'

import ImageAdHolder from '../../site/ImageAdHolder'
import BlockTitle from '../../ui/BlockTitle'
import ColorConventions from '../../ui/ColorConventions'
import HTMLPrinter from '../../ui/HTMLPrinter'
import Calendar from './Calendar'
import Ranking from './Ranking'
import { tournamentFetch } from './TournamentActions'

import './Tournament.css'

class Tournament extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    const { round, tab, tournament, tournamentID } = props
    if (tournamentID && tournamentID !== tournament.id) {
      props.tournamentFetch(tournamentID)
      return {
        round: null,
        stage: null,
        tab: state.tab || tab || 'calendar',
      }
    }
    if (tournamentID && tournamentID === tournament?.data?.[0]?.id) {
      const stages = tournament?.data?.[0]?.tournament?.stages || []
      const firstRound = state.round || round || Tournament.getFirstRound(stages)

      return {
        round: firstRound,
        stage: stages.find(({ rounds }) => rounds.some(({ id }) => id === firstRound?.id)),
        tab: state.tab || tab || 'calendar',
      }
    }

    return null
  }

  static getFirstRound(stages) {
    let actualRound
    let latestDate = 0
    const today = Math.max(moment().endOf('day').unix(), moment().add(10, 'hours').unix())

    stages.forEach((stage) => {
      const rounds = stage.rounds || []
      actualRound = actualRound || rounds[0]
      rounds.forEach((round) => {
        const matches = round.matches || []
        matches
          .filter((match) => match.matched_at && match.matched_at <= today)
          .forEach((match) => {
            const matchDate = match.ended_at || match.matched_at || match.started_at || 0
            if (matchDate > latestDate) {
              actualRound = round
              latestDate = matchDate
            }
          })
      })
    })

    return actualRound
  }

  state = {
    action: null,
    round: null,
    stage: null,
    tab: null,
  }

  onCloseMessage = () => this.setState({ action: null })

  onPrintClick = () => {
    trackEvent('Component', 'Click', 'Tournament - Print Button')
    const { tab } = this.state

    const round = tab === 'calendar' ? this.state.round : this.state.stage?.rounds?.[0]

    this.setState({
      action: (
        <HTMLPrinter onClose={this.onCloseMessage}>
          <Tournament {...this.props} printable round={round} tab={tab} />
        </HTMLPrinter>
      ),
    })
  }

  onRankingStageChange = (stage) => this.setState({ stage })

  onRoundChange = (round) => this.setState({ round })

  onTabChange = ({ target }) => this.setState({ tab: target.value })

  render() {
    const {
      avoidLink,
      className,
      device,
      fixedTab,
      highlightedMatches,
      highlightedTeams,
      printable,
      title,
      tournament,
      tournamentID,
      tournamentPage,
      user,
      widget,
    } = this.props
    const { action, stage, tab } = this.state
    let content = null
    const mainClassNames = classnames('SoccerTournament bg-white', className)

    if ((tournamentID && tournamentID !== tournament?.data?.[0]?.id) || tournament.loading) {
      return (
        <div className={mainClassNames}>
          <ProgressBar className="text-sm" value={100} />
        </div>
      )
    }

    if (!tournament?.data?.[0]?.tournament?.stages?.length) {
      return (
        <div className={mainClassNames}>
          <Tooltip tooltip="Visita la página del torneo">
            <Link
              className="flex gap-2 items-center border border-orangeLight rounded-lg bg-orangeLightest p-2 m-1 !text-text"
              to={`/sports/tournament/${tournament.data[0].id}`}
            >
              <ExclamationTriangleIcon className="text-orange size-5" />
              <div>Torneo sin información...</div>
              <TrophyIcon className="text-orange size-5" />
            </Link>
          </Tooltip>
        </div>
      )
    }

    content =
      tab === 'calendar' ? (
        <>
          {!widget && !printable && (
            <div className="p-1 text-center">
              <ColorConventions />
            </div>
          )}
          <Calendar
            highlightedMatches={highlightedMatches || []}
            highlightedTeams={highlightedMatches || []}
            onRoundChange={this.onRoundChange}
            printable={!!printable}
            round={this.state.round}
            tournament={tournament.data[0]}
            widget={widget}
          />
        </>
      ) : (
        <Ranking
          highlightedTeams={highlightedTeams}
          onStageChange={this.onRankingStageChange}
          printable={!!printable}
          stage={stage}
          tournament={tournament.data[0]}
          widget={widget}
        />
      )

    const canExport =
      !tournament.loading &&
      !device.mobile && // No mobile
      user &&
      tournamentPage?.data?.[0]?.id === tournament.data[0].id &&
      tournamentPage?.data?.[0]?.access?.level === 1 // Only admin

    return (
      <div className={classnames(mainClassNames, printable && ' printable')}>
        {action}
        {(!!title || printable) && (
          <BlockTitle
            defaultImage="tournament.jpg"
            image={tournament.data[0].avatar}
            printable={printable}
            title={tournament.data[0].name}
          />
        )}
        <div>
          {!fixedTab && !printable && (
            <div className="flex pt-1 *:p-2 *:flex *:items-center *:justify-center *:gap-1 *:cursor-pointer *:outline-0 *:rounded-t-lg *:overflow-hidden *:border">
              <button
                className={classnames(
                  'flex-1 hover:border-greenLight focus:border-greenLight hover:text-green focus:text-green',
                  tab === 'calendar'
                    ? 'text-black border-green border-b-transparent bg-white'
                    : 'text-text border-greyLightMed border-b-green bg-greyLightest',
                )}
                onClick={this.onTabChange}
                type="button"
                value="calendar"
              >
                <CalendarIcon className="size-5" /> Calendario
              </button>
              <button
                className={classnames(
                  'flex-1 hover:border-greenLight focus:border-greenLight hover:text-green focus:text-green',
                  tab === 'positions'
                    ? 'text-black border-green border-b-transparent bg-white'
                    : 'text-text border-greyLightMed border-b-green bg-greyLightest',
                )}
                onClick={this.onTabChange}
                type="button"
                value="positions"
              >
                <ListBulletIcon className="size-5" /> Posiciones
              </button>
              {canExport && (
                <Tooltip tooltip="Imprimir la tabla">
                  <button
                    className="shrink text-text border-greyLightMed border-b-green bg-greyLightest hover:border-greenLight focus:border-greenLight hover:text-green focus:text-green"
                    onClick={this.onPrintClick}
                    type="button"
                  >
                    <PrinterIcon className="size-5" />
                  </button>
                </Tooltip>
              )}
              {avoidLink ? null : (
                <Tooltip tooltip="Visita la página del torneo">
                  <Link
                    className="shrink text-[13.3333px] !text-text border-greyLightMed border-b-green bg-greyLightest hover:border-greenLight focus:border-greenLight hover:!text-green focus:!text-green"
                    to={`/sports/tournament/${tournament.data[0].id}`}
                  >
                    <TrophyIcon className="size-5" />
                  </Link>
                </Tooltip>
              )}
            </div>
          )}
          {content}
        </div>
        {printable && (
          <div className="background">
            <img alt="" src={`${APP.url}/img/sweatbits-black.png`} />
          </div>
        )}
        <div className="flex items-center justify-center">
          <div className="p-2 bg-white rounded border mt-2">
            <ImageAdHolder className="rounded block" name="Patrocinio Lista de Torneos" type="550" />
          </div>
        </div>
      </div>
    )
  }
}

Tournament.propTypes = {
  device: PropTypes.object.isRequired,
  tournament: PropTypes.object.isRequired,
  tournamentFetch: PropTypes.func.isRequired,
  tournamentID: PropTypes.string.isRequired,

  avoidLink: PropTypes.bool,
  className: PropTypes.string,
  container: PropTypes.bool,
  fixedTab: PropTypes.bool,
  highlightedMatches: PropTypes.array,
  highlightedTeams: PropTypes.array,
  printable: PropTypes.bool,
  round: PropTypes.object,
  tab: PropTypes.oneOf(['calendar', 'positions']),
  title: PropTypes.bool,
  tournamentPage: PropTypes.object,
  user: PropTypes.object,
  widget: PropTypes.bool,
}

Tournament.defaultProps = {
  avoidLink: false,
  className: '',
  container: false,
  fixedTab: false,
  highlightedMatches: [],
  highlightedTeams: [],
  printable: false,
  round: null,
  tab: 'calendar',
  title: false,
  tournamentPage: null,
  user: null,
  widget: false,
}

const mapStateToProps = ({ auth: { usr: user }, environment: { device }, tournament, tournamentPage }) => ({
  device,
  tournament,
  tournamentPage,
  user,
})

export default connect(mapStateToProps, { tournamentFetch })(Tournament)
